import { integrationModels } from './guides/integrationModels';
import { launchFlow } from './guides/launchFlow';
import { iframeEvents } from './guides/iframeEvents';
import { handlingGuide } from './guides/handlingGuide';
import { provablyFair } from './guides/provablyFair';
import { shippingPromotions } from './guides/shippingPromotions';

/**
 * OpenAPI `info` block — the landing/overview operators read first.
 * Partner-facing; rendered as markdown by Scalar. Each `##` heading becomes a
 * sidebar entry (`#description/<slug>`). The longer guides live in `guides/`
 * and are spliced in here in reading order.
 */
const description = [
  'Build your own player experience on top of Repackz card packs. This reference covers the full',
  'integration: the **wallet callbacks** your platform implements, the **server-to-server Operator API**',
  'your backend calls, and the **Player API** your front-end calls from the browser.',
  '',
  ...integrationModels,
  '## Authentication',
  '',
  'There are two credentials, depending on which side is calling.',
  '',
  '**Server-to-server (your backend).** Your backend signs every request with three headers:',
  '- `Authorization: Bearer <API_KEY>` — your per-platform API key.',
  '- `X-Repackz-Timestamp` — current Unix time in seconds.',
  '- `X-Repackz-Signature` — `sha256=` + HMAC-SHA256 of the request (see *Request signing* below).',
  '',
  'Use it to create player sessions and to read games, players and transactions. Your API key and',
  'secret are issued per platform — contact us to get onboarded.',
  '',
  '**Player API (browser).** Your front-end sends a single header:',
  '- `X-Platform-Session: <session_token>`',
  '',
  'The `session_token` is returned by **Create session** and is the only credential your UI holds.',
  'It cannot be minted in the browser: your backend creates it, then your front-end uses it on every',
  'player request.',
  '',
  '> **Trying it on this page:** a short-lived **preview** token is loaded into the auth field',
  '> automatically. You can also create a real session right here with your API key + secret — the',
  '> **Session for “Try it”** panel signs `POST /sessions` for you and loads the returned token.',
  '> Real integrations create the token from their backend the same way.',
  '',
  ...launchFlow,
  ...iframeEvents,
  ...handlingGuide,
  '## Embed mode',
  '',
  'No backend to integrate? **Embed mode** needs zero server-side work: we enable it for your account',
  '(Stripe funding + registration + an **embed key**), and you embed the player iframe as',
  '`https://<iframe_host>/?embed_key=KEY&game_id=ID`. Visitors browse packs anonymously; at the first',
  'purchase the iframe signs them up with **email + password** (forgotten passwords are reset with an',
  'emailed code). Accounts live on our side, scoped to your platform — you never store credentials.',
  'Platforms with a backend get the same real accounts by passing `user` in **Create session**. The',
  'endpoint reference lives under **Embed Mode**.',
  '',
  '## Deliveries & shipping',
  '',
  'Shipping a physical card is **handled entirely inside the player iframe** — listing/adding delivery',
  'addresses, choosing shipping options, the live fee quote (shipping + handling + tax), and payment',
  'all happen there. **You implement no shipping-specific endpoints or UI** — the iframe runs the',
  '*Deliveries* Player API for you and charges the player from their balance via your `shipping_debit`',
  'callback. (You may call the *Deliveries* API from your own UI instead, but you don’t have to.)',
  '',
  'This runs inside a normal **player session** — the same `session_token` / *Launch flow* you already',
  'use to open the game (your backend creates the session, your front-end loads the iframe with it).',
  'Shipping adds nothing beyond that session and the callbacks below.',
  '',
  'Your only shipping-specific responsibility is the **wallet callbacks**: a pack purchase opens a round',
  '(`debit`), and the round stays open until every card is resolved — sold/auto-sold back (`credit`),',
  'physically shipped (`shipping_settle`), or undeliverable → bought back (`credit` at the standard',
  'buyback price) + `refund` for the handling fee (and the card’s share of any sales tax). Details and the',
  'close rule are under **Wallet Callbacks**.',
  '',
  '### Sales tax',
  '',
  'Off by default. On request we enable sales tax on deliveries for your operator, as the seller who',
  'collects and remits it, and set up with you: the taxable base of each card — its fair market value or',
  'the price of the pack it came from — and whether the shipping and handling fees are taxed; the',
  '**states** where you collect; and, from your tax team, the tax **category** of each part of the charge',
  '(cards, shipping, insurance, signature, handling), per state where it differs. The tax is quoted for you',
  'at the player’s delivery address, as if your thresholds were met in each of those states, shown to the',
  'player as a separate **Sales tax** line (`sales_tax` in the fee quote and in `charge_breakdown`) and',
  'included in the `shipping_debit` amount. A delivery outside your states, or abroad, carries no tax.',
  '',
  'You record the tax without recalculating it. The `shipping_debit` carries it as `tax`, in cents: per part',
  'of the charge, per jurisdiction (state, county, city, district) and per flat fee, such as Colorado’s',
  'Retail Delivery Fee, which comes with a taxed shipping fee. A delivery’s',
  '`charge_breakdown.sales_tax_breakdown` on the server-to-server deliveries holds the same. Every refund',
  'returns the affected card’s share of the tax together with its fees, and its `tax` says how much of it',
  'is tax, by jurisdiction and fee. **You remit the tax** — we only compute it and pass it on; the amounts',
  'are also in the Financial report.',
  '',
  'A delivery your tax applies to is never charged without it. When the tax can’t be quoted, the fee quote',
  'and *Create a delivery* answer `503 SALES_TAX_UNAVAILABLE` (retry) or `422 SALES_TAX_ADDRESS_INVALID`',
  '(the address’s state can’t be read, or its ZIP code is in another state: the player corrects it), and',
  'nothing is charged.',
  '',
  '### Prices under a promotion',
  '',
  'Setting a promotion up, the price rules and billing are covered under **Shipping promotions** below;',
  'this is what it changes in the API.',
  '',
  'Shipping promotions are available to every operator, however you integrate. While one runs for your',
  'platform, the fee fields you already read — `shipping_fee`, `shipping_fee_components`, `handling_fee`,',
  '`option_prices` and `total` in the fee quote, and `charge_breakdown` on a delivery — **already hold the',
  'price the player pays**, so charge `total` exactly as you do without a promotion. A promotion covers the parts of',
  'the price you choose, so add-ons it leaves out stay at their regular price in those fields. Where we call your',
  'wallet, the `shipping_debit` amount is that promotional price, and a delivery that costs the player',
  'nothing sends no `shipping_debit` at all, so you never receive a zero-amount `shipping_debit`; `shipping_settle`',
  'still arrives with `amount: 0`, as for every delivery. A `promo`',
  'object next to the fee fields says what the promotion `covers` and carries the regular fees (`original_*`) and the',
  '`discount` you fund, for showing a struck-through price; it is `null` when no promotion lowers the delivery or its',
  'options. The price is fixed when the',
  'delivery is created, not when it is quoted: if a promotion starts, ends or changes in between, the',
  'created delivery’s `charge_breakdown.total` is what counts. To never charge more than the player saw,',
  'send that total as `expected_total` when you create the delivery: a price that has risen is refused with',
  '`409 SHIPPING_PRICE_CHANGED` and nothing is charged (API-only operators, who charge the player',
  'themselves, should always send it). If you charge the player yourself, compare the result with what you',
  'charged and settle any difference with the player. Every refund — a canceled delivery, an unavailable',
  'card — never exceeds what the player paid.',
  '',
  'What reaches your wallet and your webhooks: the `shipping_debit` carries `fees` — its `shipping`,',
  '`handling` and `sales_tax` parts, which add up to `amount` — and, under a promotion, a `promo` with its',
  '`covers`, `regular_amount` and `discount`, every amount in cents. `shipping.shipped`, `shipping.delivered` and',
  '`shipping.canceled` carry the same `promo` in `data` (`null` when no promotion applied), so even a free',
  'delivery, which sends no `shipping_debit`, tells you which promotion priced it.',
  '',
  ...shippingPromotions,
  '## Event webhooks',
  '',
  'Separately from the wallet callbacks, you can ask us to **notify you when something happens** —',
  '`shipping.shipped` when a player’s delivery leaves the warehouse (with its tracking number),',
  '`shipping.delivered` when it is confirmed delivered and `shipping.canceled` when it is canceled (with the',
  'reason and how each card ended). All three carry the shipping promotion that priced the delivery, as',
  '`data.promo`. You subscribe yourself in the operator console',
  'under **Webhooks**: add an HTTPS URL, tick the events you want, and press **Send test event** to',
  'confirm it answers before the first real one arrives.',
  '',
  'These are notifications, not the money protocol. Any URL you like, none of it is mandatory, and the',
  'only thing we look at is your status code — **return any 2xx** and we are done. The signature is the',
  'one you already verify for the callbacks (note that the signed path is the path of *your* URL), plus',
  '`X-Repackz-Event` and `X-Repackz-Event-Id` headers so you can route and de-duplicate without parsing',
  'the body. Delivery is at-least-once and unordered; failures retry for about half a day, and every',
  'attempt is listed in the console with a Resend button. Details are under **Event Webhooks**.',
  '',
  '## Request signing (Operator API + callbacks)',
  '',
  'Signed requests carry `X-Repackz-Timestamp` and `X-Repackz-Signature: sha256=<hex>`, where the',
  'signature is the HMAC-SHA256 of `"<timestamp>.<METHOD> <path?query>.<body>"`, keyed with your API',
  'secret, as lowercase hex (method upper-cased; body = the exact bytes sent). Timestamps must be',
  'within ±5 minutes. Every signed endpoint includes a ready-to-run **Ruby / Node.js / cURL** sample.',
  '',
  '## Response envelope',
  '',
  'The Operator API wraps successful payloads in `{ "result": ... }`. Errors everywhere use',
  '`{ "error": 1, "error_detail": { "code": "...", "message": "..." } }`. Amounts are integer **cents**',
  'in the platform currency.',
  '',
  '### New fields in existing responses',
  '',
  'The Custom Pack Lab, shipping promotions and sales tax add keys, and one new value, to responses you may already parse. A',
  'parser that ignores unknown keys needs no change; a strict schema validator has to allow them. Nothing that',
  'was there changed.',
  '',
  '- **Player API** — `game_kind` on a game (*Get a game*) and on the game embedded in every order: `loot_pack`',
  '  for a regular pack, `custom_pack` for a custom pack.',
  '- **Wallet callbacks and server-to-server calls** — `game_type` used to read `loot_pack` for every game; it now',
  '  reads `loot_pack` or `custom_pack` in the `debit`, `credit` and `refund` callbacks, in *List available games*',
  '  and in *Get a round*.',
  '  In *List available games* `price_cents` is `null` for a `custom_pack` game.',
  '- **Console API** (behind the console’s Configs page) — every pack config now carries `pool_offer_game_kind`,',
  '  `pool_offer_stated_rtp` and `stated_rtp`; for a regular pack they are `loot_pack`, `null` and `null`.',
  '- **Shipping — wallet callbacks** — `shipping_debit` carries `fees` (`shipping`, `handling` and `sales_tax`: integer',
  '  cents that add up to `amount`) on every debit from this release, and `promo` (`id`, `label`, `covers`,',
  '  `regular_amount` and `discount`; the amounts in cents) only when a shipping promotion applied.',
  '- **Shipping — event webhooks** — `data.promo` on `shipping.shipped`, `shipping.delivered` and `shipping.canceled`:',
  '  the same object, or `null` when no promotion applied.',
  '- **Shipping — Player API** — `total` and `promo` in the fee quote (*Quote shipping fees*), and',
  '  `signature` on `charge_breakdown` and `charge_breakdown.promo` on a delivery (*Get a delivery*, *Create a delivery*).',
  '- **Shipping — server-to-server calls** — `signature` on `charge_breakdown` and `charge_breakdown.promo` on the deliveries of *List a player’s deliveries*',
  '  and *Cancel a delivery*, and `fees.promo` on the rows of *List all your deliveries* and on *Get one delivery*.',
  '- **Shipping — sales tax** — only where your sales tax applies: `tax` on the `shipping_debit` and on a',
  '  shipping `refund` that returns tax, `charge_breakdown.sales_tax_breakdown` on the deliveries of',
  '  *List a player’s deliveries* and *Cancel a delivery*, and the error codes `SALES_TAX_UNAVAILABLE` and',
  '  `SALES_TAX_ADDRESS_INVALID` from the fee quote and *Create a delivery*.',
  '',
  ...provablyFair,
  '## Console access & your team',
  '',
  'This console is a separate door from the API. Your servers authenticate with the **API key +',
  'signature** described above; people sign in here with an **email and password**. Adding a colleague',
  'to the console never gives them your API credentials.',
  '',
  'Your account is the **owner** of this console: full access, and the only one who can manage the team,',
  'the integration settings and the security perimeter.',
  '',
  '### Adding people',
  '',
  'Open **Team → Add a team member**, enter a work email and tick the sections they should reach. We',
  'create a fresh console account and show a **temporary password once** — copy it and pass it on',
  'outside the console; they are asked to set their own password when they first sign in.',
  '',
  'The address must not already have a collectibles.com account. Console accounts are created by the',
  'console and belong to it alone, so an address that already exists elsewhere on the platform is',
  'refused — use a work address instead. Each person belongs to exactly one operator.',
  '',
  'Changing what someone can reach takes effect the next time their console loads. **Removing someone',
  'signs them out everywhere immediately.** If a colleague forgets their password, use **Reset password**',
  'on their row: it shows a new one-time password and ends their old sessions.',
  '',
  '### What you can grant',
  '',
  '| Section | What it opens |',
  '| --- | --- |',
  '| Packs | The read-only list of your live packs |',
  '| Configs | Pack pricing and presentation: RTP, buyback %, images, badges |',
  '| Categories | Category names, images and order shown in the iframe |',
  '| Themes | The theme editor, including its live preview |',
  '| Footer links | The links shown in the iframe footer |',
  '| Sessions & transactions | Launched player sessions and the money ledger |',
  '| Deliveries & shipping promotions | Physical-card deliveries ordered by your players, with their names and addresses, and the promotional delivery prices you set |',
  '| Callback logs | The log of calls we made to your wallet |',
  '| Test players | The registry of accounts excluded from your reporting |',
  '',
  'Three starting points are offered when you add someone — **Content**, **Operations** and',
  '**Everything** — but they are only shortcuts for ticking boxes. What gets stored is always the plain',
  'list of sections, and you can adjust it afterwards.',
  '',
  'Two of these move money, so hand them out deliberately:',
  '',
  '- **Deliveries & shipping promotions** includes reporting a card as unshippable, which buys that card back',
  '  for the player and refunds its fees, and setting promotional delivery prices, whose discount you fund.',
  '- **Themes** includes generating a preview session. A preview runs against a real player id through',
  '  your `get_session` callback, so a purchase made inside one charges that player for real.',
  '',
  '### What stays with you',
  '',
  'Never granted to a team member, whatever sections they hold:',
  '',
  '- **Integration** — callback URLs, registration mode and the embed key.',
  '- **API keys** — rotating the API secret.',
  '- **Security for the whole console** — the operator-wide IP allowlist, and requiring 2FA of everyone.',
  '- **Team** — adding, removing and re-granting people.',
  '- **Resend** on a callback log — replaying a `debit` / `credit` / `refund` at your wallet.',
  '',
  'Your API key is shown to you alone; team members never see it.',
  '',
  '### Security, per person',
  '',
  'Everyone on the team — you included — manages their own sign-in security under **Security**:',
  '',
  '- **Your sign-in IPs.** Restrict your own access to given addresses or CIDR ranges. Leave it empty',
  '  and you sign in from anywhere the operator allows.',
  '- **Two-factor authentication.** An authenticator app for your own sign-in, with recovery codes shown',
  '  once. Turning it on changes nothing for your teammates.',
  '',
  'Two settings belong to the console as a whole and stay with the owner: the **operator-wide IP',
  'allowlist**, which applies to everyone on top of their personal list, and **requiring 2FA from the',
  'whole team**. An address has to pass both allowlists to reach the console, and an empty list means',
  '"no restriction from that side". Neither the team requirement nor an enrolled authenticator can be',
  'switched off from the console — contact us.',
  '',
  'Locked out by your own IP list, or lost the phone with your authenticator? Contact us and we will',
  'clear it for the person affected.',
].join('\n');

export const info = {
  title: 'Repackz Game Platform API',
  version: '1.0.0',
  description,
} as const;
